import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { treeProblemSolutionDtoSchema } from './problem-solution.dto.js'
import { problemSeveritySchema } from '../enums/problem-severity.enum.js'
import { seasonSchema } from '../enums/season.enum.js'

/**
 * One problem found on one tree.
 *
 * A tree carries a LIST of these rather than a flag and a note, because that
 * is what a tree actually has. Mistletoe in the crown and a split leader at
 * the base are two findings with two remedies and two urgencies, and folding
 * them into one "has a problem, here are some notes" loses the ability to say
 * which of them is the reason to come out this month.
 *
 * `typeId` is null for a problem that is not in the directory — the "Other"
 * case. Null rather than a sentinel row for the same reason an unidentified
 * species is null: a sentinel is something somebody can rename or archive, and
 * "this is not one of our catalogued problems" is the absence of a type rather
 * than a type of its own.
 */
export const treeProblemDtoSchema = z.object({
  id: uuidSchema,
  /** The directory entry, or null when this is free-text "Other". */
  typeId: uuidSchema.nullable(),
  /**
   * What is specifically true of THIS tree.
   *
   * Required for "Other", where it is the whole description, and optional for
   * a catalogued problem, where the type already says what the problem is and
   * this adds what is particular — "only the north side", "over the footpath".
   */
  notes: z.string().nullable(),
  /**
   * How bad this problem is on this tree, 1-5, or null when it is flagged but
   * not yet rated.
   *
   * Per problem rather than per tree. A tree with light mistletoe and a
   * cracked limb over a car park is a 2 and a 5 at once, and a single number
   * would have to pick one — which is the number somebody then sorts a work
   * list by.
   */
  severity: problemSeveritySchema.nullable(),
  /**
   * When this problem is dealt with (docs/seasonal-work.md): mistletoe in
   * spring on a tree pruned in winter. Where a problem with no treatment goes
   * on a proposal, and the season a treatment chosen for it starts in. Null
   * when no season was chosen, and then it goes with the tree's pruning.
   * Defaulted for a tree a phone cached before problems had seasons.
   */
  season: seasonSchema.nullable().default(null),
  /**
   * The treatments chosen for this problem on this tree.
   *
   * Each carries the money it works out to, computed by the API: the figure
   * depends on the organization's labor rate and on the tree's diameter, and
   * a total assembled in two places is a total that will disagree with itself.
   */
  solutions: z.array(treeProblemSolutionDtoSchema),
  /**
   * What dealing with this problem on this tree costs, set by hand, in place
   * of the sum of its treatments. Null is that sum. Defaulted for a tree a
   * phone cached before prices could be set.
   */
  priceOverride: moneyAmountSchema.nullable().default(null),
  /**
   * The problem on the tree's last visit this one was brought forward from by
   * "Still here", or null for one found fresh (docs/site-visits.md). How a
   * problem that keeps coming back has a history. Defaulted for a tree a
   * phone cached before there was one.
   */
  previousId: uuidSchema.nullable().default(null),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type TreeProblemDto = z.infer<typeof treeProblemDtoSchema>
